import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from "./shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import * as Sentry from "@sentry/react";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, onAuthStateChanged, type User } from "firebase/auth";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import "./i18n";
import { CompanyProvider } from "./contexts/CompanyContext";
import { DesignSystemProvider } from "./contexts/DesignSystemContext";
import { getTrpcUrl } from "./lib/api-config";
import {
  initializeErrorTracking,
  captureApiError,
  captureComponentError,
} from "./lib/error-tracking";
import { initializeMonitoring } from "./lib/monitoring";

console.log("[App] Initializing with tRPC URL:", getTrpcUrl());

// Initialize Firebase for auth token retrieval
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: "auth.swisstax.work",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Only initialize if Firebase is configured
let firebaseAuth: ReturnType<typeof getAuth> | null = null;
let currentUser: User | null = null;
let authReady = false;
let authReadyPromise: Promise<void> | null = null;

if (
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId
) {
  const app =
    getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  firebaseAuth = getAuth(app);

  // Create a promise that resolves when auth state is ready
  authReadyPromise = new Promise<void>(resolve => {
    const unsubscribe = onAuthStateChanged(firebaseAuth!, user => {
      currentUser = user;
      if (!authReady) {
        authReady = true;
        console.log(
          "[Firebase] Auth state ready, user:",
          user?.email || "none"
        );
        resolve();
      }
    });
  });
}

// Helper to get current Firebase token - waits for auth to be ready
async function getFirebaseToken(): Promise<string | null> {
  if (!firebaseAuth) return null;

  // Wait for auth state to be initialized (with timeout)
  if (!authReady && authReadyPromise) {
    const timeout = new Promise<void>(resolve => setTimeout(resolve, 2000));
    await Promise.race([authReadyPromise, timeout]);
  }

  // Use tracked currentUser instead of firebaseAuth.currentUser
  if (!currentUser) return null;
  try {
    return await currentUser.getIdToken();
  } catch {
    return null;
  }
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);

    // Capture API errors with Sentry
    if (error instanceof TRPCClientError) {
      captureApiError(error as any, {
        procedure: "query",
        path: window.location.pathname,
        method: "GET",
      });
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);

    // Capture API errors with Sentry
    if (error instanceof TRPCClientError) {
      captureApiError(error as any, {
        procedure: "mutation",
        path: window.location.pathname,
        method: "POST",
      });
    }
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: getTrpcUrl(),
      transformer: superjson,
      async headers() {
        // Include Firebase token in Authorization header if available
        const token = await getFirebaseToken();
        if (token) {
          return {
            Authorization: `Bearer ${token}`,
          };
        }
        return {};
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Initialize error tracking before rendering
initializeErrorTracking();

// Initialize Firebase Analytics & Performance monitoring
initializeMonitoring();

// Load analytics script if environment variables are configured
const loadAnalytics = () => {
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;

  if (endpoint && websiteId) {
    const script = document.createElement("script");
    script.defer = true;
    script.src = `${endpoint}/umami`;
    script.setAttribute("data-website-id", websiteId);
    document.head.appendChild(script);
    console.log("[Analytics] Umami analytics loaded");
  } else {
    console.log("[Analytics] Skipped - env vars not configured");
  }
};

loadAnalytics();

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ error, componentStack, resetError }) => (
      <div style={{ padding: "2rem", maxWidth: "800px", margin: "0 auto" }}>
        <h1 style={{ color: "var(--destructive)", marginBottom: "1rem" }}>
          Something went wrong
        </h1>
        <p style={{ marginBottom: "1rem" }}>
          An error occurred in the application. Our team has been notified and
          we're working to fix it.
        </p>
        <details style={{ marginBottom: "1rem" }}>
          <summary style={{ cursor: "pointer", color: "var(--primary)" }}>
            Error Details
          </summary>
          <pre
            style={{
              backgroundColor: "var(--muted)",
              padding: "1rem",
              borderRadius: "0.5rem",
              overflow: "auto",
              marginTop: "0.5rem",
            }}
          >
            {error instanceof Error ? error.message : String(error)}
            {componentStack}
          </pre>
        </details>
        <button
          onClick={resetError}
          style={{
            backgroundColor: "var(--primary)",
            color: "white",
            padding: "0.5rem 1rem",
            borderRadius: "0.375rem",
            border: "none",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    )}
    onError={(error, componentStack) => {
      console.error("[ErrorBoundary]", error, componentStack);
      captureComponentError(
        error instanceof Error ? error : new Error(String(error)),
        "Root",
        componentStack ?? undefined
      );
    }}
  >
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <CompanyProvider>
          <DesignSystemProvider>
            <App />
          </DesignSystemProvider>
        </CompanyProvider>
      </QueryClientProvider>
    </trpc.Provider>
  </Sentry.ErrorBoundary>
);
